import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { PERSONA_INFO } from '../../data/mockData';
import { useTheme } from '../../context/ThemeContext';

const PersonaChart = ({ data }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const chartData = data.filter(d => d.revenue > 0);

  const getColor = (persona) => PERSONA_INFO[persona]?.color || '#94a3b8';

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className={`p-4 border shadow-2xl rounded-xl text-xs z-50 min-w-[220px]
          ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
          <p className={`font-bold text-sm mb-3 pb-2 border-b ${isDark ? 'text-white border-white/10' : 'text-slate-900 border-slate-200'}`}>{d.id}</p>
          <div className="space-y-2">
            <div className="flex justify-between"><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Manager:</span> <span className={`font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{d.manager}</span></div>
            <div className="flex justify-between"><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Revenue:</span> <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>${d.revenue.toLocaleString()}</span></div>
            <div className="flex justify-between"><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Emails:</span> <span className={`font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{d.messagesReceived.toLocaleString()}</span></div>
            <div className="flex justify-between"><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Variance:</span> <span className={`font-semibold ${d.revWorkloadVariance >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>${d.revWorkloadVariance.toLocaleString(undefined, {maximumFractionDigits: 0})}</span></div>
          </div>
          <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
             <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: `${getColor(d.persona)}20`, color: getColor(d.persona) }}>{d.persona}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex flex-wrap gap-2 mb-4 text-[10px] justify-center sm:justify-start">
        {Object.keys(PERSONA_INFO).filter(p=>p!=='Unknown').map((p) => {
            const item = PERSONA_INFO[p];
            return (
              <div key={p} className={`flex items-center px-2.5 py-1.5 rounded-lg border
                ${isDark ? 'border-white/5 bg-white/[0.02]' : 'border-slate-200 bg-slate-50'}`}>
                <span className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: item.color }}></span>
                <span className={isDark ? 'font-medium text-slate-400' : 'font-medium text-slate-600'}>{p}</span>
              </div>
            )
        })}
      </div>
      
      <div className="flex-grow">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 10, bottom: 30, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} stroke={isDark ? '#ffffff' : '#000000'} />
            <XAxis 
              type="number" 
              dataKey="messagesReceived" 
              name="Emails" 
              tick={{fontSize: 11, fill: '#64748b'}}
              axisLine={{stroke: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}}
              tickLine={false}
              label={{ value: 'Annual Email Volume', position: 'insideBottom', offset: -20, fontSize: 11, fill: '#64748b' }} 
            />
            <YAxis 
              type="number" 
              dataKey="revenue" 
              name="Revenue" 
              tick={{fontSize: 11, fill: '#64748b'}}
              axisLine={{stroke: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}}
              tickLine={false}
              tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`}
              label={{ value: 'Annual Revenue ($)', angle: -90, position: 'insideLeft', offset: 0, fontSize: 11, fill: '#64748b' }} 
            />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3', stroke: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' }} />
            
            <Scatter name="Clients" data={chartData} fill="#8884d8">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getColor(entry.persona)} strokeWidth={2} stroke={isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)'} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PersonaChart;
